"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_IMPORT_SOURCE = exports.importVersionSchema = exports.importSheetSchema = exports.importMappingSchema = exports.importRowsQuerySchema = exports.IMPORT_ROW_FILTERS = exports.IMPORT_PREVIEW_PEOPLE_MAX = exports.IMPORT_TARGET_LABELS = exports.IMPORT_TARGETS = exports.CV_AUTO_RETRIES = exports.CV_RETRY_DELAY_MS = exports.CV_PROBLEM_LABELS = exports.CV_PROBLEM_MESSAGES = exports.IMPORT_MESSAGES = exports.IMPORT_PAGE_SIZE = exports.IMPORT_EXTENSIONS = exports.IMPORT_PREVIEW_ROWS = exports.IMPORT_MAX_ROWS = exports.IMPORT_MAX_BYTES = void 0;
exports.tooManyRowsMessage = tooManyRowsMessage;
exports.isCvDownloadProblem = isCvDownloadProblem;
exports.normaliseHeader = normaliseHeader;
exports.isImportTarget = isImportTarget;
exports.columnMappingOf = columnMappingOf;
exports.guessMapping = guessMapping;
exports.headerSignatureInput = headerSignatureInput;
exports.mappingProblems = mappingProblems;
exports.columnOf = columnOf;
exports.guessCriterionColumns = guessCriterionColumns;
exports.coverageNote = coverageNote;
exports.criterionColumnsByLabel = criterionColumnsByLabel;
exports.rowKeyInput = rowKeyInput;
exports.splitLinks = splitLinks;
exports.isFolderLink = isFolderLink;
exports.convertShareLink = convertShareLink;
exports.educationLevelOf = educationLevelOf;
exports.experienceMonthsOf = experienceMonthsOf;
exports.salaryOf = salaryOf;
exports.noticeDaysOf = noticeDaysOf;
exports.appliedOnOf = appliedOnOf;
exports.rowValuesOf = rowValuesOf;
exports.importedDetailsOf = importedDetailsOf;
exports.standardFromImportRow = standardFromImportRow;
exports.importSummaryLine = importSummaryLine;
exports.previewPeopleOf = previewPeopleOf;
exports.previewPeopleLines = previewPeopleLines;
const zod_1 = require("zod");
const applications_1 = require("./applications");
const admin_config_1 = require("./admin-config");
const enums_1 = require("./enums");
const schemas_1 = require("./schemas");
// §L29 — CSV import (R4, D18). Pure rules shared by the API and IMP-01/IMP-02.
// --- File limits (§L26 "Data import") -------------------------------------------------------
exports.IMPORT_MAX_BYTES = 20 * 1024 * 1024;
exports.IMPORT_MAX_ROWS = 5000;
exports.IMPORT_PREVIEW_ROWS = 10;
exports.IMPORT_EXTENSIONS = ['csv', 'xlsx'];
exports.IMPORT_PAGE_SIZE = 50;
/** §L26 "Import and module messages", word for word. */
exports.IMPORT_MESSAGES = {
    noHeader: 'The first row must name the columns. Add a header row and upload again.',
    notUtf8: "This file isn't saved as UTF-8, so names could come out garbled. In Excel: Save As → CSV UTF-8, then upload again.",
    nameNotMatched: 'Choose the column that holds the name.',
    noEmailOrPhone: 'Choose a column for email or phone, so duplicates can be checked.',
    rowNoContact: "No email or phone — imported, but duplicates can't be checked.",
    // Not in §L26's table; written to its rules (what happened, what to do next).
    wrongType: "That isn't a CSV or Excel file. Upload a .csv or .xlsx file.",
    tooLarge: 'This file is larger than 20 MB. Split it and upload the parts.',
    noRows: 'This file has column names but no rows under them.',
    unreadableXlsx: "This Excel file couldn't be opened. Save it again in Excel, or save it as CSV UTF-8, and upload again.",
};
function tooManyRowsMessage(rows) {
    return `This file has ${rows.toLocaleString('en-US')} rows. The most per file is 5,000 — split it and upload the parts.`;
}
/** §L26: each §L29 problem's message, shown in IMP-02's problems table. */
exports.CV_PROBLEM_MESSAGES = {
    link_private: "The CV link is private. Share it as 'Anyone with the link' and Retry, or upload the CV here.",
    not_found: 'Nothing is at that link any more. Ask the candidate for their CV and upload it here.',
    not_a_cv_file: "That link isn't a PDF or Word file. Upload the CV here.",
    too_large: 'The CV is larger than 10 MB. Upload a smaller copy here.',
    timed_out: "The CV link didn't answer. Trying again in 5 minutes.",
    bad_link: "That isn't a web link. Upload the CV here.",
    // Workflow review D4: a folder can hold anything, so nothing is fetched and Retry is not offered.
    folder_link: "That link opens a Google Drive folder, not a CV file. Upload the CV here, or ask for a link to the file itself.",
};
/** The short names the problems table's filter uses (PRD IM-6). */
exports.CV_PROBLEM_LABELS = {
    link_private: 'Link is private',
    not_found: 'File not found',
    not_a_cv_file: 'Not a PDF or Word file',
    too_large: 'File too large',
    timed_out: "Link didn't answer",
    bad_link: 'Not a web link',
    folder_link: 'Folder, not a file',
};
/** §L29: `timed_out` retries itself once; every other problem waits for a person. */
exports.CV_RETRY_DELAY_MS = 5 * 60 * 1000;
exports.CV_AUTO_RETRIES = 1;
function isCvDownloadProblem(value) {
    return enums_1.CV_DOWNLOAD_PROBLEMS.includes(value);
}
// --- Column matching (§L29) --------------------------------------------------------------------
exports.IMPORT_TARGETS = [
    'name',
    'first_name',
    'last_name',
    'email',
    'phone',
    'cv_link',
    'source',
    'applied_on',
    'education',
    'skills',
    'years_experience',
    'expected_salary',
    'notice_period',
    'location',
    'portfolio_link',
];
exports.IMPORT_TARGET_LABELS = {
    name: 'Name',
    first_name: 'First name',
    last_name: 'Last name',
    email: 'Email',
    phone: 'Phone',
    cv_link: 'CV link',
    source: 'Source',
    applied_on: 'Applied on',
    education: 'Education',
    skills: 'Skills',
    years_experience: 'Years of experience',
    expected_salary: 'Expected salary',
    notice_period: 'Notice period',
    location: 'Location',
    portfolio_link: 'Portfolio or GitHub link',
};
/**
 * Headers as the guesser compares them (§L29): lower case, accents and punctuation gone,
 * spaces collapsed. "Upload your CV:" and "upload your cv" are one header.
 */
function normaliseHeader(header) {
    return header
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        // An abbreviation's dots go ("M.Sc." → "msc", §L30); a word's stay a break ("github.com").
        .replace(/(?<![\p{L}\p{N}])(\p{L}{1,2})\./gu, '$1')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}
/**
 * §L29's synonym table. Phrases match a whole header, or appear inside one as whole words.
 * Built from Fluent Forms, Google Forms and job-site exports' usual wording, until the
 * owner's real exports arrive (DECISIONS-LOG "Start R4" Q1).
 */
const TARGET_SYNONYMS = {
    name: ['name', 'names', 'full name', 'your name', 'your full name', 'candidate name', 'applicant name', 'name of applicant', 'name of the applicant'],
    first_name: ['first name', 'firstname', 'given name', 'name first'],
    last_name: ['last name', 'lastname', 'surname', 'family name', 'name last'],
    email: ['email', 'e mail', 'email address', 'e mail address', 'your email', 'your email address', 'email id', 'mail'],
    phone: ['phone', 'phone number', 'mobile', 'mobile number', 'mobile no', 'phone no', 'contact number', 'contact no', 'cell', 'cell phone', 'whatsapp', 'whatsapp number', 'your phone', 'your phone number'],
    cv_link: ['cv', 'resume', 'cv link', 'resume link', 'upload cv', 'upload your cv', 'upload resume', 'upload your resume', 'attach cv', 'attach your cv', 'cv resume', 'resume cv', 'curriculum vitae', 'file upload'],
    source: ['source', 'how did you hear about us', 'where did you hear about us', 'how did you hear about this job', 'where did you see this job'],
    applied_on: ['submitted at', 'submitted on', 'submission date', 'date submitted', 'timestamp', 'applied on', 'applied at', 'application date', 'created at', 'entry date'],
    education: ['education', 'highest degree', 'degree', 'highest education', 'qualification', 'highest qualification', 'educational qualification', 'last degree', 'last academic degree'],
    skills: ['skills', 'key skills', 'technical skills', 'skill set', 'skillset', 'your skills'],
    years_experience: ['years of experience', 'experience', 'total experience', 'experience years', 'years experience', 'work experience', 'experience in years', 'total years of experience'],
    expected_salary: ['expected salary', 'salary expectation', 'expected salary bdt', 'expected salary tk', 'desired salary', 'salary', 'expected monthly salary'],
    notice_period: ['notice period', 'notice', 'when can you join', 'joining time', 'earliest joining date', 'available to join'],
    location: ['location', 'city', 'current city', 'current location', 'present address', 'address', 'district', 'where do you live'],
    portfolio_link: ['github', 'github profile', 'github link', 'github url', 'portfolio', 'portfolio link', 'portfolio url', 'website', 'personal website', 'linkedin', 'linkedin profile'],
};
/** A single word that marks a header wherever it appears in it ("CV (PDF only)" is the CV). */
const STRONG_WORDS = {
    cv_link: ['cv', 'resume'],
    email: ['email'],
    phone: ['phone', 'mobile', 'whatsapp'],
    portfolio_link: ['github', 'portfolio', 'linkedin'],
    expected_salary: ['salary'],
    skills: ['skills'],
};
function isImportTarget(value) {
    return typeof value === 'string' && exports.IMPORT_TARGETS.includes(value);
}
/** A stored matching, read back: anything that isn't a target is "not matched". */
function columnMappingOf(stored) {
    return Object.fromEntries(Object.entries(stored).map(([header, target]) => [header, isImportTarget(target) ? target : null]));
}
function containsPhrase(header, phrase) {
    return ` ${header} `.includes(` ${phrase} `);
}
function exactTarget(header) {
    return exports.IMPORT_TARGETS.find((target) => TARGET_SYNONYMS[target].includes(header)) ?? null;
}
/** The longest multi-word synonym inside the header, or a strong word in it. */
function partialTarget(header) {
    let best = null;
    for (const target of exports.IMPORT_TARGETS) {
        const phrases = [...TARGET_SYNONYMS[target].filter((phrase) => phrase.includes(' ')), ...(STRONG_WORDS[target] ?? [])];
        for (const phrase of phrases) {
            if (containsPhrase(header, phrase) && (!best || phrase.length > best.length))
                best = { target, length: phrase.length };
        }
    }
    return best?.target ?? null;
}
/**
 * §L29 "Auto-guess": an exact synonym first, then a phrase inside the header. Each target
 * goes to one column only, the first that claims it; everything else stays unmatched and
 * is kept as Imported details.
 */
function guessMapping(headers) {
    const mapping = Object.fromEntries(headers.map((header) => [header, null]));
    const taken = new Set();
    const claim = (header, target) => {
        if (!target || taken.has(target) || mapping[header] !== null)
            return;
        mapping[header] = target;
        taken.add(target);
    };
    for (const header of headers)
        claim(header, exactTarget(normaliseHeader(header)));
    for (const header of headers)
        claim(header, partialTarget(normaliseHeader(header)));
    return mapping;
}
/** What the header signature is a hash of: the sorted, normalised headers (§L29 "Remembered"). */
function headerSignatureInput(headers) {
    return [...headers].map(normaliseHeader).sort().join('\n');
}
/** §L25 `uploaded → mapped`: a name, plus email or phone, so duplicates can be checked. */
function mappingProblems(mapping) {
    const targets = new Set(Object.values(mapping));
    const problems = {};
    if (!targets.has('name') && !targets.has('first_name'))
        problems.name = exports.IMPORT_MESSAGES.nameNotMatched;
    if (!targets.has('email') && !targets.has('phone'))
        problems.contact = exports.IMPORT_MESSAGES.noEmailOrPhone;
    return problems;
}
/** One target per column and one column per target: the screen's Select can't do otherwise. */
function columnOf(mapping, target) {
    return Object.entries(mapping).find(([, value]) => value === target)?.[0] ?? null;
}
const STOP_WORDS = new Set([
    'a', 'an', 'the', 'of', 'in', 'on', 'at', 'to', 'for', 'and', 'or', 'your', 'you', 'do', 'does', 'what', 'is', 'are',
    'with', 'com', 's', 'how', 'many', 'have', 'has', 'this', 'job', 'why', 'want', 'please', 'any', 'i', 'my',
]);
const DEGREE_WORDS = ['degree', 'masters', 'master', 'bachelor', 'bachelors', 'msc', 'bsc', 'mba', 'bba', 'phd', 'diploma', 'education', 'hsc', 'ssc', 'graduate'];
const LINK_WORDS = ['github', 'portfolio', 'linkedin', 'website'];
const FREE_TEXT_HINTS = ['free time', 'hobby', 'hobbies', 'interest', 'interests', 'leisure', 'spare time', 'about yourself', 'about you'];
const LEVEL_HINTS = ['rate', 'level', '1 5', '1 10'];
const MIN_GUESS_SCORE = 2;
function words(text) {
    return normaliseHeader(text)
        .split(' ')
        .filter((word) => word.length > 0 && !STOP_WORDS.has(word));
}
function hintScore(criterion, header, target, criterionWords) {
    const normalised = normaliseHeader(header);
    const has = (list) => list.some((word) => criterionWords.has(word));
    if (criterion.kind === 'yes_no' && has(DEGREE_WORDS) && target === 'education')
        return 3;
    if (criterion.kind === 'years' && target === 'years_experience')
        return 3;
    if (criterion.kind === 'keywords' && has(LINK_WORDS) && target === 'portfolio_link')
        return 3;
    if (criterion.kind === 'keywords' && target === 'skills')
        return 1;
    if (criterion.kind === 'mentioned' && FREE_TEXT_HINTS.some((hint) => containsPhrase(normalised, hint)))
        return 2;
    if (criterion.kind === 'level' && LEVEL_HINTS.some((hint) => containsPhrase(normalised, hint)))
        return 1;
    return 0;
}
function columnScore(criterion, header, target) {
    const criterionWords = new Set([...words(criterion.label), ...criterion.lookFor.flatMap(words)]);
    const overlap = words(header).filter((word) => criterionWords.has(word)).length;
    // A level hint ("Rate your…") only helps a header that already names the skill.
    const hint = hintScore(criterion, header, target, criterionWords);
    if (criterion.kind === 'level' && overlap === 0)
        return 0;
    return overlap * 2 + hint;
}
/**
 * The automatic first answer to "where will each criterion be read from?". A saved choice
 * for a criterion of the same name wins (§L29 "Remembered"); otherwise the header sharing
 * the most words with the criterion, helped by what its kind usually reads. A criterion
 * no header fits is "CV text only".
 */
function guessCriterionColumns(criteria, mapping, saved = {}) {
    const headers = Object.keys(mapping);
    return Object.fromEntries(criteria.map((criterion) => {
        const remembered = saved[normaliseHeader(criterion.label)];
        if (remembered !== undefined && (remembered === null || headers.includes(remembered)))
            return [criterion.key, remembered];
        let best = null;
        for (const header of headers) {
            const score = columnScore(criterion, header, mapping[header] ?? null);
            if (score >= MIN_GUESS_SCORE && (!best || score > best.score))
                best = { header, score };
        }
        return [criterion.key, best?.header ?? null];
    }));
}
/** What the coverage list says under a criterion with no column (§L29, §L30 "Years: CV not used"). */
function coverageNote(kind, column) {
    if (column !== null)
        return `Read from the column "${column}"`;
    if (kind === 'years')
        return "No column, and the CV's text isn't searched for years";
    return "Only the CV's text will be searched";
}
/** Criterion columns keyed by criterion name, so the next job with the same criterion gets them. */
function criterionColumnsByLabel(criteria, columns) {
    return Object.fromEntries(criteria.filter((criterion) => criterion.key in columns).map((criterion) => [normaliseHeader(criterion.label), columns[criterion.key] ?? null]));
}
// --- Rows ---------------------------------------------------------------------------------------
/**
 * What §L29's row key is a hash of: the job, the normalised email or else phone, and the
 * row's values. Hashed by the API; the same row in the same job is the same key.
 */
function rowKeyInput(jobId, email, phone, values) {
    const contact = email.trim() !== '' ? (0, schemas_1.normaliseEmail)(email) : phoneOrEmpty(phone);
    return JSON.stringify([jobId, contact, values.map((value) => value.trim())]);
}
const CV_LINK_EXTENSIONS = ['pdf', 'doc', 'docx'];
const LINK_SEPARATORS = /[\s,;]+/;
const WEB_LINK = /^https?:\/\/\S+$/i;
function extensionOfLink(link) {
    const path = link.replace(/[?#].*$/, '');
    const last = path.slice(path.lastIndexOf('/') + 1);
    const dot = last.lastIndexOf('.');
    return dot < 0 ? '' : last.slice(dot + 1).toLowerCase();
}
function cvIndexOf(links) {
    const byExtension = links.findIndex((link) => CV_LINK_EXTENSIONS.includes(extensionOfLink(link)));
    if (byExtension >= 0)
        return byExtension;
    const shareLink = links.findIndex((link) => extensionOfLink(link) === '');
    return shareLink >= 0 ? shareLink : 0;
}
/** §L29 "The source today": a file-upload cell may hold several links, split by commas, spaces or new lines. */
function splitLinks(cell) {
    const parts = cell.split(LINK_SEPARATORS).filter((part) => part !== '');
    if (parts.length === 0)
        return { cv: null, others: [], isBadLink: false };
    const links = parts.filter((part) => WEB_LINK.test(part));
    if (links.length === 0)
        return { cv: null, others: [], isBadLink: true };
    const index = cvIndexOf(links);
    return { cv: links[index] ?? null, others: links.filter((_, at) => at !== index), isBadLink: false };
}
// Google puts an account part (`u/0/`) before `d/` or `folders/` when the person sharing has more than one account signed in.
const DRIVE_FILE = /^https?:\/\/drive\.google\.com\/file\/(?:u\/\d+\/)?d\/([^/?#]+)/i;
const DRIVE_OPEN = /^https?:\/\/drive\.google\.com\/open\?(?:.*&)?id=([^&#]+)/i;
/** Workflow review D4: a Google Docs document, which has no file to download until it is exported. */
const GOOGLE_DOC = /^https?:\/\/docs\.google\.com\/document\/(?:u\/\d+\/)?d\/([^/?#]+)/i;
const DRIVE_FOLDER = /^https?:\/\/drive\.google\.com\/(?:drive\/(?:u\/\d+\/)?folders\/|folderview\?(?:.*&)?id=)/i;
/** Workflow review D4: a Google Drive folder link. There is no one file behind it, so it is never fetched. */
function isFolderLink(link) {
    return DRIVE_FOLDER.test(link);
}
/**
 * §L29 "CV download": a share link becomes its download link. Google Drive's `/file/d/{id}`
 * and `open?id={id}` → `uc?export=download`; a Google Docs document → its PDF export (workflow
 * review D4); Dropbox `dl=0` → `dl=1`; OneDrive gets `download=1` (best effort). Anything else is
 * fetched as it is. A private link still comes back as Google's sign-in page, which §L29 reads as private.
 */
function convertShareLink(link) {
    const driveFile = DRIVE_FILE.exec(link);
    if (driveFile?.[1])
        return `https://drive.google.com/uc?export=download&id=${driveFile[1]}`;
    const driveOpen = DRIVE_OPEN.exec(link);
    if (driveOpen?.[1])
        return `https://drive.google.com/uc?export=download&id=${driveOpen[1]}`;
    const googleDoc = GOOGLE_DOC.exec(link);
    if (googleDoc?.[1])
        return `https://docs.google.com/document/d/${googleDoc[1]}/export?format=pdf`;
    if (/^https?:\/\/(www\.)?dropbox\.com\//i.test(link))
        return link.replace(/([?&])dl=0\b/, '$1dl=1');
    if (/^https?:\/\/(1drv\.ms|onedrive\.live\.com)\//i.test(link) && !/[?&]download=1\b/.test(link)) {
        return `${link}${link.includes('?') ? '&' : '?'}download=1`;
    }
    return link;
}
// --- Values (§L29 "Each row", into §L2's standard fields) ----------------------------------------
const MIN_PHONE_DIGITS = 6;
function phoneOrEmpty(raw) {
    return raw.replace(/\D/g, '').length < MIN_PHONE_DIGITS ? '' : (0, schemas_1.normalisePhone)(raw);
}
function emailOrEmpty(raw) {
    const trimmed = raw.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) ? trimmed : '';
}
/** §L30's degree ladder and synonyms: "M.Sc." → "msc" → Master's. The strongest found wins. */
const EDUCATION_WORDS = [
    ['doctorate', ['phd', 'doctorate', 'dphil']],
    ['masters', ['masters', 'master', 'msc', 'mba', 'ma', 'mphil', 'mcom', 'mss', 'meng', 'mtech']],
    ['bachelors', ['bachelors', 'bachelor', 'bsc', 'bba', 'ba', 'bcom', 'bss', 'beng', 'btech', 'llb', 'mbbs', 'honours', 'hons', 'graduate']],
    ['diploma', ['diploma']],
    ['hsc', ['hsc', 'alim', 'a level', 'a levels']],
    ['ssc', ['ssc', 'dakhil', 'o level', 'o levels']],
];
function educationLevelOf(text) {
    const normalised = normaliseHeader(text);
    if (normalised === '')
        return null;
    return EDUCATION_WORDS.find(([, synonyms]) => synonyms.some((word) => containsPhrase(normalised, word)))?.[0] ?? null;
}
const MONTHS_PER_YEAR = 12;
const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;
const NONE_WORDS = /^(fresher|fresh|none|no|nil|n a|na|0)$/;
function numberOf(match) {
    return Number(`${match[1]}.${match[2] ?? '0'}`);
}
/** "2", "1.5 years", "6 months", "2 years 5 months", "Fresher" → months; anything else is unknown. */
function experienceMonthsOf(text) {
    const normalised = normaliseHeader(text.replace(/(\d)\.(\d)/g, '$1 point $2'));
    if (NONE_WORDS.test(normalised))
        return 0;
    // D84: years and months together, with no other number (a range such as "3-5 years" reads as before).
    const years = /(\d+)(?: point (\d+))? ?(?:years?|yrs?|y)\b/.exec(normalised);
    const months = /(\d+)(?: point (\d+))? ?(?:months?|mos?|m)\b/.exec(normalised);
    if (years && months && normalised.match(/\d+(?: point \d+)?/g)?.length === 2) {
        return Math.round(numberOf(years) * MONTHS_PER_YEAR + numberOf(months));
    }
    const number = /(\d+)(?: point (\d+))?/.exec(normalised);
    if (!number?.[1])
        return null;
    const value = numberOf(number);
    return Math.round(/\bmonths?\b/.test(normalised) ? value : value * MONTHS_PER_YEAR);
}
/** "45,000", "35000 BDT", "40k" → whole taka; "Negotiable" is unknown (D13: asked in BDT). */
function salaryOf(text) {
    const compact = text.toLowerCase().replace(/,/g, '');
    const number = /(\d+(?:\.\d+)?)\s*(k)?\b/.exec(compact);
    if (!number?.[1])
        return null;
    const value = Number(number[1]) * (number[2] ? 1000 : 1);
    return Number.isFinite(value) && value > 0 ? Math.round(value) : null;
}
/** "Immediately", "2 weeks", "1 month", "30 days" → days. */
function noticeDaysOf(text) {
    const normalised = normaliseHeader(text);
    if (/^(immediately|immediate|now|asap|0)$/.test(normalised))
        return 0;
    const number = /(\d+)/.exec(normalised);
    if (!number?.[1])
        return null;
    const value = Number(number[1]);
    if (/\bweeks?\b/.test(normalised))
        return value * DAYS_PER_WEEK;
    if (/\bmonths?\b/.test(normalised))
        return value * DAYS_PER_MONTH;
    return value;
}
/** D14: an export's times are Bangladesh times, which never change for daylight saving. */
const BANGLADESH_UTC_OFFSET = '+06:00';
/** "2026-10-05 14:32[:10]" → that moment in UTC (non-negotiable #8). Other shapes stay in Imported details. */
function appliedOnOf(text) {
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(text.trim());
    if (!match)
        return null;
    const [, year, month, day, hour = '00', minute = '00', second = '00'] = match;
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}${BANGLADESH_UTC_OFFSET}`);
    return Number.isNaN(date.getTime()) ? null : date;
}
function listOf(text) {
    return text
        .split(/[,;/\n]+/)
        .map((item) => item.trim())
        .filter((item) => item !== '');
}
function rowValuesOf(mapping, row) {
    const values = {};
    for (const [header, target] of Object.entries(mapping)) {
        if (target)
            values[target] = (row[header] ?? '').trim();
    }
    return values;
}
/** §L29 "Kept, not thrown away": every column not matched to a target, with a value. */
function importedDetailsOf(mapping, row) {
    return Object.fromEntries(Object.entries(row).filter(([header, value]) => (mapping[header] ?? null) === null && value.trim() !== ''));
}
function fullNameOf(values) {
    const whole = values.name ?? '';
    if (whole !== '')
        return whole.replace(/\s+/g, ' ');
    return [values.first_name ?? '', values.last_name ?? ''].join(' ').replace(/\s+/g, ' ').trim();
}
/**
 * One row in the shape the career site's parser produces (F22.04: "the intake path"), so
 * §L2 recognises an imported person exactly as it recognises an applicant.
 */
function standardFromImportRow(values) {
    const portfolio = values.portfolio_link ?? '';
    return {
        fullName: fullNameOf(values),
        email: emailOrEmpty(values.email ?? ''),
        phone: phoneOrEmpty(values.phone ?? ''),
        totalExperienceMonths: values.years_experience ? experienceMonthsOf(values.years_experience) : null,
        highestEducation: values.education ? educationLevelOf(values.education) : null,
        skills: listOf(values.skills ?? ''),
        expectedSalary: values.expected_salary ? salaryOf(values.expected_salary) : null,
        noticePeriodDays: values.notice_period ? noticeDaysOf(values.notice_period) : null,
        city: values.location ? values.location : null,
        currentEmployer: null,
        currentTitle: null,
        isWillingToRelocate: null,
        linkedinUrl: /linkedin\.com/i.test(portfolio) ? portfolio : null,
        languages: [],
        certifications: [],
        dateOfBirth: null,
        gender: null,
        nationalId: null,
    };
}
function plural(count, one, many) {
    return `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;
}
/**
 * IMP-02's summary line, word for word as §L29's worked example reads:
 * "980 imported · 20 already on this job · CVs: 950 attached, 27 need you, 3 had no link".
 */
function importSummaryLine(counts) {
    const parts = [`${(counts.created + counts.linked).toLocaleString('en-US')} imported`];
    if (counts.alreadyApplied > 0)
        parts.push(`${counts.alreadyApplied.toLocaleString('en-US')} already on this job`);
    if (counts.alreadyImported > 0)
        parts.push(`${plural(counts.alreadyImported, 'row', 'rows')} already imported`);
    if (counts.failed > 0)
        parts.push(`${counts.failed.toLocaleString('en-US')} couldn't be imported`);
    const cvs = [`${counts.cvAttached.toLocaleString('en-US')} attached`];
    if (counts.cvQueued > 0)
        cvs.push(`${counts.cvQueued.toLocaleString('en-US')} downloading`);
    if (counts.cvRetrying > 0)
        cvs.push(`${counts.cvRetrying.toLocaleString('en-US')} trying again`);
    if (counts.cvNeedYou > 0)
        cvs.push(`${counts.cvNeedYou.toLocaleString('en-US')} need you`);
    if (counts.cvNone > 0)
        cvs.push(`${counts.cvNone.toLocaleString('en-US')} had no link`);
    return `${parts.join(' · ')} · CVs: ${cvs.join(', ')}`;
}
// --- Who the file brings (IM-3, workflow review D3) ------------------------------------------------
/** The most names each preview group lists; the count is always the whole file's. */
exports.IMPORT_PREVIEW_PEOPLE_MAX = 200;
/**
 * §L2 tiers 2 and 3 as the preview can tell them from the file alone: the same email, or the same phone with a
 * name at least 0.85 alike. Tiers 1 (national ID, never in a file) and 4–6 (a flag, not a link) don't link.
 */
function isSamePerson(row, other) {
    if (row.email !== '' && row.email === other.email)
        return true;
    return row.phone !== '' && row.phone === other.phone && (0, applications_1.trigramSimilarity)(row.name, other.name) >= applications_1.NAME_SIMILARITY_WITH_PHONE;
}
function groupOf(people) {
    return { count: people.length, people: people.slice(0, exports.IMPORT_PREVIEW_PEOPLE_MAX) };
}
/**
 * Workflow review D3: who the file brings, as the import will treat them. "Already in your candidate bank" is not a
 * duplicate on this job: those people are added to it and linked to their profile. Only someone already on this job
 * (or a second row for the same person) is skipped.
 */
function previewPeopleOf(rows, known) {
    const bank = known.map((person) => ({ email: person.email ?? '', phone: person.phone ?? '', name: person.normalisedName ?? '', isOnThisJob: person.isOnThisJob }));
    const earlier = [];
    const inBank = [];
    const onThisJob = [];
    const repeated = [];
    let newCount = 0;
    for (const row of rows) {
        const keys = { email: row.email, phone: row.phone, name: (0, schemas_1.normaliseName)(row.name) };
        const person = { rowNumber: row.rowNumber, name: row.name === '' ? `Row ${row.rowNumber}` : row.name };
        const isRepeat = earlier.some((other) => isSamePerson(keys, other));
        earlier.push(keys);
        if (isRepeat) {
            repeated.push(person);
            continue;
        }
        const match = bank.filter((other) => isSamePerson(keys, other));
        if (match.some((other) => other.isOnThisJob))
            onThisJob.push(person);
        else if (match.length > 0)
            inBank.push(person);
        else
            newCount += 1;
    }
    return { inBank: groupOf(inBank), onThisJob: groupOf(onThisJob), repeated: groupOf(repeated), newCount };
}
/** "102 are already in your candidate bank…": one line per group, in plain words, for IMP-01's preview. */
function previewPeopleLines(people) {
    const isOne = (count) => count === 1;
    const count = (value) => value.toLocaleString('en-US');
    return {
        inBank: people.inBank.count === 0
            ? null
            : `${count(people.inBank.count)} ${isOne(people.inBank.count) ? 'is' : 'are'} already in your candidate bank. They’ll be added to this job and linked to their profile.`,
        onThisJob: people.onThisJob.count === 0
            ? null
            : `${count(people.onThisJob.count)} already applied to this job, so ${isOne(people.onThisJob.count) ? 'that row' : 'those rows'} will be skipped.`,
        repeated: people.repeated.count === 0
            ? null
            : `${count(people.repeated.count)} ${isOne(people.repeated.count) ? 'row repeats' : 'rows repeat'} someone earlier in this file. Only the first is added.`,
        newPeople: `${count(people.newCount)} ${isOne(people.newCount) ? 'is' : 'are'} new.`,
    };
}
exports.IMPORT_ROW_FILTERS = ['needs_you', 'skipped', 'all'];
exports.importRowsQuerySchema = zod_1.z.object({
    filter: zod_1.z.enum(exports.IMPORT_ROW_FILTERS).default('needs_you'),
    page: zod_1.z.coerce.number().int().positive().default(1),
});
exports.importMappingSchema = zod_1.z.object({
    columns: zod_1.z.record(zod_1.z.string(), zod_1.z.enum(exports.IMPORT_TARGETS).nullable()),
    criteria: zod_1.z.record(zod_1.z.string().uuid(), zod_1.z.string().nullable()),
    source: admin_config_1.sourceKeySchema,
    version: zod_1.z.number().int().min(1),
});
exports.importSheetSchema = zod_1.z.object({ sheet: zod_1.z.string().min(1), version: zod_1.z.number().int().min(1) });
exports.importVersionSchema = zod_1.z.object({ version: zod_1.z.number().int().min(1) });
/** The source an upload starts with, when the person hasn't picked one (ADMIN-08 key). */
exports.DEFAULT_IMPORT_SOURCE = 'website_form';
//# sourceMappingURL=imports.js.map